"use client";

import { useEffect, useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { useBlockchain } from "@/providers/global-data.provider";
import {mainConfig} from "@/configs/main.config";

// Calculate emission schedule data with simplified approach
const calculateEmissionSchedule = () => {
  const schedule = [];

  // Initial parameters
    const {
        blocksPerYear,
        newRewardApplyBlock,
        reductionInterval,
        reductionFactor,
        weeksPerYear,
        initialReward,
    } = mainConfig.rewardSystem;

  // We're skipping year 1 (current year) and starting from year 2

  // Calculate for years 2-16 (15 years) using a simplified approach
  for (let year = 1; year <= 15; year++) {
    // Calculate the reward at the start of the year
    const weeksElapsedStart = (year - 1) * weeksPerYear;
    let startReward = initialReward;

    // Apply weekly reductions for start of year
    startReward *= Math.pow(reductionFactor, weeksElapsedStart);

    // Calculate the reward at the end of the year
    const weeksElapsedEnd = year * weeksPerYear;
    let endReward = initialReward;

    // Apply weekly reductions for end of year
    endReward *= Math.pow(reductionFactor, weeksElapsedEnd);

    // Apply staking split if applicable
    if (year > 2) { // Simplification - year 3+ has staking
      startReward *= 0.8;
      endReward *= 0.8;
    }

    // Calculate average reward for the year
    const avgReward = (startReward + endReward) / 2;

    // Calculate yearly emission (approximate)
    const yearlyEmission = avgReward * blocksPerYear;

    // Calculate block numbers
    const startBlock = newRewardApplyBlock + (weeksElapsedStart * reductionInterval);
    const endBlock = newRewardApplyBlock + (weeksElapsedEnd * reductionInterval);

    // Add to schedule
    schedule.push({
      year: year + 1,
      startBlock,
      endBlock,
      startBlockReward: startReward.toFixed(4),
      endBlockReward: endReward.toFixed(4),
      yearlyEmission: Math.round(yearlyEmission).toLocaleString(),
      avgBlockReward: avgReward.toFixed(4)
    });
  }

  return schedule;
};

export function EmissionScheduleTable() {
  const { currentBlockReward, currentBlock, isLoading, error } = useBlockchain();
  const [ emissionSchedule, setEmissionSchedule ] = useState<{
      year: number;
      startBlock: number;
      endBlock: number;
      startBlockReward: string;
      endBlockReward: string;
      yearlyEmission: string;
      avgBlockReward: string;
  }[]>([]);

  useEffect(() => {
    if (!isLoading && !error) {
      const schedule = calculateEmissionSchedule();
      setEmissionSchedule(schedule);
    }
  }, [currentBlock, currentBlockReward, isLoading, error]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#FF8A3C] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading emission schedule...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-40">
        <p className="text-red-500">Error loading emission schedule data</p>
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]">Year</TableHead>
            <TableHead>Block Range</TableHead>
            <TableHead>Start Reward</TableHead>
            <TableHead>End Reward</TableHead>
            <TableHead>Avg Reward</TableHead>
            <TableHead className="text-right">Yearly Emission</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {emissionSchedule.map((year) => {
            // Check if current block falls within this year's range
            const isCurrentYear = currentBlock >= year.startBlock && currentBlock <= year.endBlock;

            return (
              <TableRow
                key={year.year}
                className={isCurrentYear ? "bg-[#FF8A3C]/10 border-l-4 border-l-[#FF8A3C]" : ""}
              >
                <TableCell className={`font-medium ${isCurrentYear ? "text-[#FF8A3C] font-bold" : ""}`}>
                  {year.year}
                  {isCurrentYear && <span className="ml-2 text-xs">(Current)</span>}
                </TableCell>
                <TableCell className={isCurrentYear ? "font-semibold" : ""}>
                  {year.startBlock.toLocaleString()} - {year.endBlock.toLocaleString()}
                </TableCell>
                <TableCell className={isCurrentYear ? "font-semibold" : ""}>
                  {year.startBlockReward} LRS
                </TableCell>
                <TableCell className={isCurrentYear ? "font-semibold" : ""}>
                  {year.endBlockReward} LRS
                </TableCell>
                <TableCell className={isCurrentYear ? "font-semibold text-[#FF8A3C]" : ""}>
                  {year.avgBlockReward} LRS
                </TableCell>
                <TableCell className={`text-right ${isCurrentYear ? "font-semibold" : ""}`}>
                  {year.yearlyEmission} LRS
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
